'use client';

import dynamic from 'next/dynamic';
import BlogPost from '@/components/sections/builder/elements/body/blog-post';
import Hero from '@/components/sections/builder/elements/body/hero';
import Announcement from '@/components/sections/builder/elements/header/announcement';
import ImageWithText from '@/components/sections/builder/elements/body/image-with-text';
import ImageBanner from '@/components/sections/builder/elements/body/image-banner';
import Header from '@/components/sections/builder/elements/header/header';
import ProductList from '@/components/sections/builder/elements/body/product-grid';
import Video from '@/components/sections/builder/elements/body/video';
import ImageGallery from '@/components/sections/builder/elements/body/image-gallery';
import Slideshow from '@/components/sections/builder/elements/body/slideshow';
import ScrollingText, {
  ScrollingTextHeader,
} from '@/components/sections/builder/elements/body/scrolling-text';
import Footer from '@/components/sections/builder/elements/footer/footer';
import Multicolumn from '@/components/sections/builder/elements/body/multicolumn';
import CollectionList from '@/components/sections/builder/elements/body/collection-list';
import TextWithIcon from '@/components/sections/builder/elements/body/text-with-icon';
import ProductGrid from '@/components/sections/builder/elements/body/collection-product-grid';
import ProductDetail from '@/components/sections/builder/elements/body/product-detail';
import { ElementObjectType, ElementListType } from '@/components/sections/builder/types';
import CartItem from '@/components/sections/builder/elements/body/cart-item';
import CartTotal from '@/components/sections/builder/elements/body/cart-total';
import Collage from '@/components/sections/builder/elements/body/collage';
import CheckoutForm from '@/components/sections/builder/elements/body/checkout-form';
import ProductCarousel from '@/components/sections/builder/elements/body/product-carousel';
import CollectionListPage from '@/components/sections/builder/elements/body/collection-list-page';
import ContentPages from '@/components/sections/builder/elements/body/content-pages';
import CollectionBanner from '@/components/sections/builder/elements/body/collection-banner';
import HeaderCheckout from '@/components/sections/builder/elements/header/header-checkout';
import Signin from '@/components/sections/builder/elements/body/signin';
import Signup from '@/components/sections/builder/elements/body/signup';
import BlogDetail from '@/components/sections/builder/elements/body/blog-detail';
import BlogHome from '@/components/sections/builder/elements/body/blog-home';
import CustomerDetail from '@/components/sections/builder/elements/body/customer/customer_detail';
import CustomerOrder from '@/components/sections/builder/elements/body/customer/customer_order';
import CustomerAddress from '@/components/sections/builder/elements/body/customer/customer_address';
import ThankYou from '@/components/sections/builder/elements/body/thankyou';
import FooterCopyright from '@/components/sections/builder/elements/footer/footer-copyright';
import SearchPage from '@/components/sections/builder/elements/body/search';
import CustomerOrderDetail from '@/components/sections/builder/elements/body/customer/customer-order-detail';
import AboutUs from '@/components/sections/builder/elements/body/about-us';
import AboutUsStoreLocation from '@/components/sections/builder/elements/body/store-location';
import PopupMakerConfig from '@/components/sections/builder/elements/body/popup-maker';
import RelatedProduct from '@/components/sections/builder/elements/body/related-product';
import NotFound from '@/components/sections/builder/elements/body/not-found';
import CollectionTab from '@/components/sections/builder/elements/body/collection-tab';
import RichText from '@/components/sections/builder/elements/body/rich-text';
import Timeline from '@/components/sections/builder/elements/body/timeline';
import ProductReview from '@/components/sections/builder/elements/body/product-review';
import CustomerReview from '@/components/sections/builder/elements/body/customer/customer_review';

const PopupMaker = {
  ...PopupMakerConfig,
  Element: dynamic(
    () =>
      import('@/components/sections/builder/elements/body/popup-maker').then(
        (mod) => mod.default.Element
      ),
    { ssr: false }
  ),
};

const elementBuilder = (elements: ElementListType[]) =>
  elements.reduce((result, val) => {
    result[val.type] = val;
    return result;
  }, {} as ElementObjectType);

export const ELEMENT_GROUPS = [
  { group: 'header', title: 'Header' },
  { group: 'header_checkout', title: 'Header Checkout' },
  { group: 'body', title: 'Body' },
  { group: 'body_collections', title: 'Body Collections' },
  { group: 'body_collection_tab', title: 'Body Collection Tab' },
  { group: 'body_rich_text', title: 'Body Rich Text' },
  { group: 'body_collection_list', title: 'Body Collection List' },
  { group: 'body_product_detail', title: 'Body Product Detail' },
  { group: 'body_pages', title: 'Body Pages' },
  { group: 'body_cart', title: 'Body Cart' },
  { group: 'body_blog_post', title: 'Blog Post' },
  { group: 'body_blog_detail', title: 'Blog Detail' },
  { group: 'body_checkout', title: 'Body Checkout' },
  { group: 'body_thankyou', title: 'Body Thankyou' },
  { group: 'body_signin', title: 'Body Signin' },
  { group: 'body_signup', title: 'Body Signup' },
  { group: 'body_customer_detail', title: 'Body Customer Detail' },
  { group: 'body_customer_orders', title: 'Body Customer Order' },
  { group: 'body_customer_orders_detail', title: 'Body Customer Order Detail' },
  { group: 'body_customer_address', title: 'Body Customer Address' },
  { group: 'body_customer_review', title: 'Body Customer Review' },
  { group: 'body_search', title: 'Body Search' },
  { group: 'body_about_us', title: 'Body About Us' },
  { group: 'body_timeline', title: 'Body Timeline' },
  { group: 'body_not_found', title: 'Body Not Found' },
  { group: 'footer', title: 'Footer' },
];

const ELEMENT_LIST = [
  Announcement,
  Hero,
  BlogPost,
  BlogDetail,
  ImageWithText,
  ImageBanner,
  Header,
  ProductList,
  ProductCarousel,
  Video,
  ImageGallery,
  Slideshow,
  ScrollingText,
  ScrollingTextHeader,
  CollectionList,
  CollectionTab,
  Footer,
  Multicolumn,
  TextWithIcon,
  ProductGrid,
  ProductDetail,
  CartItem,
  CartTotal,
  Collage,
  CollectionListPage,
  ContentPages,
  CheckoutForm,
  CollectionBanner,
  HeaderCheckout,
  Signin,
  Signup,
  BlogHome,
  CustomerDetail,
  CustomerOrder,
  CustomerAddress,
  ThankYou,
  FooterCopyright,
  SearchPage,
  CustomerOrderDetail,
  AboutUs,
  AboutUsStoreLocation,
  PopupMaker,
  RelatedProduct,
  NotFound,
  RichText,
  Timeline,
  ProductReview,
  CustomerReview,
] as ElementListType[];

export const ElementComponents = elementBuilder(ELEMENT_LIST);

export const SettingComponents = elementBuilder(ELEMENT_LIST.filter(({ Setting }) => Setting));
